import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../commons/Auth';
import {
  Button,
  Col,
  CustomInput,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { BtnOutlineGreen, BtnOutlineRed } from '../../../styled-components';

const INITIAL_EXTERNAL_AGREEMENT = {
  name: '',
  activity: '',
  supplier: '',
  file: '',
  expireDate: '',
};

const INITIAL_EXTERNAL_AGREEMENT_ERRORS = {
  name: false,
  activity: false,
  supplier: false,
  file: false,
};

function NewExternalAgreementModal({
  profile,
  activities,
  suppliers,
  onConfirm,
  fetchSuppliers,
}) {
  const [t] = useTranslation('EXTERNAL_AGREEMENTS_ADMIN_SUPPLIER');
  const alert = useAlert();
  const [{ newIsAdmin } = {}] = useAuth();

  const [modal, setModal] = useState(false);
  const [formValues, setFormValues] = useState(INITIAL_EXTERNAL_AGREEMENT);
  const [errors, setErrors] = useState(INITIAL_EXTERNAL_AGREEMENT_ERRORS);

  const [isExternalSupplier, setIsExternalSupplier] = useState(false);

  const toggle = () => {
    setModal(!modal);
    setErrors(INITIAL_EXTERNAL_AGREEMENT_ERRORS);
    setFormValues({
      ...INITIAL_EXTERNAL_AGREEMENT,
    });
    setIsExternalSupplier(false);
  };

  function handleNewContract() {
    const name = formValues.name;
    const activity = formValues.activity;
    const supplier = formValues.supplier;
    const file = formValues.file;
    const agreementType = 'SUPPLIER_ADMIN';

    let newErrors = {};
    if (name === '') newErrors.name = true;
    if (activity === '') newErrors.activity = true;
    if (isExternalSupplier === false && supplier === '')
      newErrors.supplier = true;
    if (file === '') newErrors.file = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
      return;
    }

    const fileSize = (file.size / 1024 ** 2).toFixed(2);

    if (fileSize > 100) {
      alert.info('File troppo grande. Max 100Mb');
      toggle();
    } else {
      const data = new FormData();
      data.append('name', name);
      data.append('activity', activity);
      data.append('supplier', supplier);
      data.append('file', file);
      data.append('agreementType', agreementType);
      data.append('isExternalSupplier', isExternalSupplier);

      onConfirm(data, toggle);
    }
  }

  const { name, activity, supplier } = formValues;

  useEffect(() => {
    if (activity && activity !== '') {
      fetchSuppliers(activity);
    }
  }, [activity]);

  return (
    <React.Fragment>
      <Button
        color="primary"
        style={{
          margin: 0,
          fontSize: '1rem',
          width: '100%',
        }}
        onClick={(event) => {
          event.stopPropagation();
          toggle();
        }}
      >
        {t('BTN_TITLE')}
      </Button>

      <Modal
        isOpen={modal}
        toggle={toggle}
        className="new-file-modal agreement-label"
        style={{ maxWidth: '70%' }}
      >
        <ModalHeader toggle={toggle} style={{ paddingBottom: '0px' }}>
          {t('TITLE')}
        </ModalHeader>

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleNewContract();
          }}
        >
          <ModalBody>
            <Row>
              <Col sm="12">
                <FormGroup>
                  <Label>{t('NAME')}</Label>
                  <Input
                    type="text"
                    name="name"
                    maxLength="50"
                    defaultValue={name}
                    placeholder={t('SELECT_NAME')}
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        name: e.target.value,
                      });
                      setErrors({
                        ...errors,
                        name: false,
                      });
                    }}
                  />
                  {errors.name === true && (
                    <FormText className="error-message">
                      {t('NAME_ERROR_REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col sm="12">
                <FormGroup>
                  <Label>{t('ACTIVITY')}</Label>
                  <CustomInput
                    id="activity"
                    type="select"
                    name="activity"
                    value={activity}
                    placeholder={t('SELECT')}
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        activity: e.target.value,
                        type: '',
                        typeName: '',
                        supplier: '',
                        type: '',
                      });
                      setErrors({
                        ...errors,
                        activity: false,
                      });
                    }}
                    style={{
                      color: activity === '' ? '#aaa' : '#000',
                    }}
                  >
                    <option value="" className="placeholder-option">
                      Seleziona un'attività
                    </option>
                    {activities.map((activity, i) => (
                      <option key={i} value={activity._id}>
                        {activity.name}
                      </option>
                    ))}
                  </CustomInput>
                  {errors.activity === true && (
                    <FormText className="error-message">
                      {t('ACTIVITY_ERROR_REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col sm="12" md="6">
                <FormGroup>
                  <Label>{t('IS_EXTERNAL_SUPPLIER')}</Label>
                  <div
                    onChange={(e) => {
                      setIsExternalSupplier(e.target.value === '1');
                      setFormValues({
                        ...formValues,
                        supplier: '',
                      });
                    }}
                  >
                    <CustomInput
                      type="radio"
                      id="isExternalSupplierRadio1"
                      label="SI"
                      name="isExternalSupplier"
                      value="1"
                      inline
                      checked={isExternalSupplier}
                    />
                    <CustomInput
                      type="radio"
                      id="isExternalSupplierRadio2"
                      label="NO"
                      name="isExternalSupplier"
                      value="0"
                      inline
                      checked={!isExternalSupplier}
                    />
                  </div>
                </FormGroup>
              </Col>

              <Col sm="12">
                <FormGroup>
                  <Label>{t('SUPPLIER')}</Label>
                  <CustomInput
                    id="supplier"
                    type="select"
                    name="supplier"
                    value={supplier}
                    placeholder={t('SELECT')}
                    disabled={
                      (!activity && activity === '') ||
                      isExternalSupplier === true
                    }
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        supplier: e.target.value,
                      });
                      setErrors({
                        ...errors,
                        supplier: false,
                      });
                    }}
                    style={{
                      color: supplier === '' ? '#aaa' : '#000',
                    }}
                  >
                    <option value="" className="placeholder-option">
                      Seleziona un fornitore
                    </option>
                    {suppliers.map((supplier, i) => (
                      <option key={i} value={supplier._id}>
                        {supplier.businessName}
                      </option>
                    ))}
                  </CustomInput>
                  {errors.supplier === true && (
                    <FormText className="error-message">
                      {t('SUPPLIER_ERROR_REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col sm="12">
                <FormGroup>
                  <Label for="selectContract">
                    {t('FILES:CHOOSE_FILE')}
                    &nbsp;({t('FILES:MAX_UPLOAD_FILE_SIZE')})
                  </Label>
                  <CustomInput
                    type="file"
                    id="file"
                    name="file"
                    accept=".pdf,application/pdf"
                    onChange={(event) =>
                      setFormValues({
                        ...formValues,
                        file: event.target.files[0],
                      })
                    }
                    accept=".pdf,application/pdf"
                  />
                  {errors.file === true && (
                    <FormText className="error-message">
                      {t('FILE_ERROR_REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <BtnOutlineRed type="button" className="uppercase" onClick={toggle}>
              {t('COMMON:CANCEL')}
            </BtnOutlineRed>
            <BtnOutlineGreen type="submit" className="uppercase">
              {t('COMMON:CONFIRM')}
            </BtnOutlineGreen>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
}

export default NewExternalAgreementModal;
